// src/pages/Alerts.jsx
// AgentBlayna – Real Estate Alerts
// New leads, follow-ups due, deals closing soon, and life events (birthdays/anniversaries).
// ✅ Fetches clients (required) + also pulls backend alerts (/api/alerts)
// ✅ Merges and dedupes alerts so something ALWAYS shows when data exists
// ✅ Persisted dismiss for BACKEND alerts only (A): /api/alerts/{id}/dismiss
// ✅ Life-event windows: 0-7 High, 8-15 Medium, 16-31 Low, 32+ hidden
// ✅ Live relative timestamps on cards

import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Stack,
  Grid,
  Typography,
  Chip,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Tooltip,
  Divider,
  Checkbox,
  Drawer,
  Avatar,
  InputAdornment,
  Collapse, // ✅ ADDITIVE
} from "@mui/material";
import { useOutletContext } from "react-router-dom";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import SnoozeRoundedIcon from "@mui/icons-material/SnoozeRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import CakeRoundedIcon from "@mui/icons-material/CakeRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded"; // ✅ ADDITIVE
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded"; // ✅ ADDITIVE

import { API_ENDPOINTS } from "../config";
import { apiFetch } from "../api/http";


/* -------------------- helpers -------------------- */

// ✅ Robust parse for FastAPI date-only strings (YYYY-MM-DD) so it doesn't shift in US timezones
function parseDate(d) {
  if (!d) return null;

  // Handle date-only strings from FastAPI (YYYY-MM-DD) as LOCAL date
  if (typeof d === "string") {
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const day = Number(m[3]);
      return new Date(y, mo, day);
    }
  }

  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function daysBetween(a, b) {
  const ms = a.getTime() - b.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

// days until next occurrence of month/day (birthday/anniversary)
function daysUntilThisYear(dateStr, today = new Date()) {
  const base = parseDate(dateStr);
  if (!base) return null;

  const t = new Date(today);
  t.setHours(0, 0, 0, 0);

  const target = new Date(t);
  target.setMonth(base.getMonth(), base.getDate());
  target.setHours(0, 0, 0, 0);

  if (target < t) target.setFullYear(target.getFullYear() + 1);

  return Math.round((target.getTime() - t.getTime()) / (1000 * 60 * 60 * 24));
}

function formatName(c) {
  const name = `${c.first_name || ""} ${c.last_name || ""}`.trim();
  return name || "Unnamed client";
}

function normalizeSeverity(sev) {
  const s = String(sev || "").toLowerCase();
  if (s === "priority") return "Priority";
  if (s === "high") return "High";
  if (s === "medium") return "Medium";
  if (s === "low") return "Low";
  return "Medium";
}

/**
 * ✅ Life-event severity buckets (authoritative per your spec)
 * 0–7 => High
 * 8–15 => Medium
 * 16–31 => Low
 * 32+ => null (hide)
 */
function lifeEventSeverityBucket(days) {
  if (days == null) return null;
  if (days < 0) return null;
  if (days <= 7) return "High";
  if (days <= 15) return "Medium";
  if (days <= 31) return "Low";
  return null;
}

function safeCsvCell(v) {
  const s = v == null ? "" : String(v);
  // escape quotes + wrap if contains comma/newline/quote
  if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function isLikelyIso(s) {
  return typeof s === "string" && /\d{4}-\d{2}-\d{2}T/.test(s);
}

function relativeTimeFromIso(iso, now = new Date()) {
  const dt = parseDate(iso) || (isLikelyIso(iso) ? new Date(iso) : null);
  if (!dt) return "";

  const diffMs = now.getTime() - dt.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin <= 0) return "just now";
  if (diffMin === 1) return "1 minute ago";
  if (diffMin < 60) return `${diffMin} minutes ago`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours === 1) return "1 hour ago";
  if (diffHours < 24) return `${diffHours} hours ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "yesterday";
  return `${diffDays} days ago`;
}

/* -------------------- ADDITIVE helpers (NEW) -------------------- */

function summarizeSeverities(list) {
  const base = { Priority: 0, High: 0, Medium: 0, Low: 0 };
  (list || []).forEach((a) => {
    if (base[a?.severity] != null) base[a.severity] += 1;
  });
  return base;
}

function formatSeveritySummary(counts) {
  if (!counts) return "";
  return Object.entries(counts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => `${v} ${k}`)
    .join(" • ");
}

/**
 * Resolve generic "event" wording into real event types when possible.
 * Uses: type, summary, detail, source, source_type (if present).
 * Frontend-only; no backend changes required.
 */
function resolveEventLabel(alert) {
  if (!alert) return alert;

  const rawType = String(alert.type || "");
  const rawTypeLc = rawType.toLowerCase();

  const blob = [
    alert.type,
    alert.summary,
    alert.detail,
    alert.source,
    alert.source_type,
    alert.sourceType,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  // Only override if it looks generic ("event"/"upcoming event")
  const looksGenericEvent =
    rawTypeLc === "event" ||
    rawTypeLc.includes("upcoming event") ||
    rawTypeLc === "upcoming";

  if (!looksGenericEvent) return alert;

  if (blob.includes("open house")) return { ...alert, type: "Open house" };
  if (blob.includes("photography")) return { ...alert, type: "Photography" };
  if (blob.includes("inspection")) return { ...alert, type: "Inspection" };
  if (blob.includes("appraisal")) return { ...alert, type: "Appraisal" };
  if (blob.includes("walkthrough")) return { ...alert, type: "Final walkthrough" };
  if (blob.includes("showing")) return { ...alert, type: "Showing" };
  if (blob.includes("closing")) return { ...alert, type: "Closing" };

  return alert;
}

/* -------------------- build alerts from CLIENTS (frontend rules) -------------------- */

function buildClientAlerts(clientsRaw) {
  const now = new Date();
  const alerts = [];

  (clientsRaw || []).forEach((c, idx) => {
    const name = formatName(c);
    const stageRaw = (c.stage || "").toLowerCase();
    const stageLabel = c.stage || "No stage set";
    const typeLabel = c.client_type || "";
    const property =
      c.property_address || c.listing_address || c.search_area || "";

    const createdAtRaw = c.created_at || c.createdAt;
    const lastContactRaw =
      c.last_contacted_at ||
      c.last_contact ||
      c.last_contact_date ||
      c.last_touched_at;
    const closeDateRaw =
      c.close_date || c.expected_close_date || c.contract_close_date;

    const createdAt = parseDate(createdAtRaw);
    const lastContact = parseDate(lastContactRaw);
    const closeDate = parseDate(closeDateRaw);

    const daysSinceCreated =
      createdAt != null ? daysBetween(now, createdAt) : null;
    const daysSinceContact =
      lastContact != null ? daysBetween(now, lastContact) : null;
    const daysToClose =
      closeDate != null ? -daysBetween(now, closeDate) : null; // positive = future

    const leadSourceRaw = c.lead_source || "";
    const leadSourceLc = leadSourceRaw.toLowerCase();

    const isClosed = stageRaw.includes("closed") || stageRaw.includes("won") || stageRaw === "past client";
    const isLost = stageRaw.includes("lost");

    // --- New lead alert (created in last 2 days, still a lead) ---
    if (
      daysSinceCreated != null &&
      daysSinceCreated <= 2 &&
      (stageRaw.includes("lead") || stageRaw === "" || stageRaw.includes("new"))
    ) {
      let severity = "Medium";
      if (leadSourceLc.includes("open house")) severity = "Priority";

      alerts.push({
        id: `NEW-${c.id || idx}`,
        kind: "NEW_LEAD",
        type: "New lead",
        severity,
        clientId: c.id,
        clientName: name,
        stage: stageLabel,
        clientType: typeLabel || "Lead",
        property,
        createdAt: createdAt ? createdAt.toLocaleString() : "",
        summary: `New lead from ${leadSourceRaw || "unknown source"}.`,
        detail:
          "Reach out within 24 hours to book a call or showing while interest is high.",
        source: leadSourceRaw || "Lead capture",
        status: "Open",
        lastContact,
        daysSinceContact,
        daysToClose,
        createdAtRaw: createdAt ? createdAt.toISOString() : now.toISOString(),
        closeDateRaw,
        // Not persisted
        persisted: false,
      });
    }

    // ✅ Follow-up needed if NO last contact date exists (so you always get alerts w/ sparse data)
    if (!isClosed && !isLost && lastContact == null) {
      const alreadyNewLead =
        daysSinceCreated != null &&
        daysSinceCreated <= 2 &&
        (stageRaw.includes("lead") || stageRaw === "" || stageRaw.includes("new"));

      if (!alreadyNewLead) {
        alerts.push({
          id: `FU0-${c.id || idx}`,
          kind: "FOLLOW_UP",
          type: "Follow-up",
          severity: "Medium",
          clientId: c.id,
          clientName: name,
          stage: stageLabel,
          clientType: typeLabel || "Client",
          property,
          createdAt: now.toLocaleString(),
          summary: `No follow-up activity logged yet for ${name}.`,
          detail:
            "Log the last contact date or send a quick check-in so this client doesn’t go cold.",
          source: "Activity",
          status: "Open",
          lastContact: null,
          daysSinceContact: null,
          daysToClose,
          createdAtRaw: now.toISOString(),
          closeDateRaw,
          persisted: false,
        });
      }
    }

    // --- Follow-up needed (no contact for X days, not closed/lost) ---
    if (!isClosed && !isLost && daysSinceContact != null) {
      if (daysSinceContact >= 7) {
        let severity = "Low";
        if (daysSinceContact >= 21) severity = "High";
        else if (daysSinceContact >= 14) severity = "Medium";

        alerts.push({
          id: `FU-${c.id || idx}`,
          kind: "FOLLOW_UP",
          type: "Follow-up",
          severity,
          clientId: c.id,
          clientName: name,
          stage: stageLabel,
          clientType: typeLabel || "Client",
          property,
          createdAt: lastContact
            ? lastContact.toLocaleString()
            : createdAt
            ? createdAt.toLocaleString()
            : "",
          summary: `Haven't touched base with ${name} in ${daysSinceContact} days.`,
          detail:
            "Send a quick check-in, new listing suggestion, or status update to keep the relationship warm.",
          source: "Activity",
          status: "Open",
          lastContact,
          daysSinceContact,
          daysToClose,
          createdAtRaw: lastContact
            ? lastContact.toISOString()
            : createdAt
            ? createdAt.toISOString()
            : now.toISOString(),
          closeDateRaw,
          persisted: false,
        });
      }
    }

    // --- Deal closing soon (under contract and close date within 14 days) ---
    const underContract =
      stageRaw.includes("contract") || stageRaw.includes("escrow");
    if (underContract && daysToClose != null && daysToClose >= 0) {
      if (daysToClose <= 14) {
        let severity = "Low";
        if (daysToClose <= 3) severity = "High";
        else if (daysToClose <= 7) severity = "Medium";

        alerts.push({
          id: `CLOSE-${c.id || idx}`,
          kind: "CLOSING_SOON",
          type: "Deal closing soon",
          severity,
          clientId: c.id,
          clientName: name,
          stage: stageLabel,
          clientType: typeLabel || "Client",
          property,
          createdAt: closeDate ? closeDate.toLocaleString() : "",
          summary: `Closing expected in ${daysToClose} day${
            daysToClose === 1 ? "" : "s"
          }.`,
          detail:
            "Confirm docs, walkthroughs, and key dates so there are no surprises before closing.",
          source: "Contract",
          status: "Open",
          lastContact,
          daysSinceContact,
          daysToClose,
          createdAtRaw: closeDate ? closeDate.toISOString() : now.toISOString(),
          closeDateRaw,
          persisted: false,
        });
      }
    }

    // --- Birthdays & Anniversaries (life events) ---
    const birthdayRaw = c.birthday || c.birthday_date || c.dob;
    const anniversaryRaw = c.anniversary || c.anniversary_date;

    const daysUntilBirthday =
      birthdayRaw != null ? daysUntilThisYear(birthdayRaw, now) : null;
    const daysUntilAnniversary =
      anniversaryRaw != null ? daysUntilThisYear(anniversaryRaw, now) : null;

    const pushLifeEvent = (opts) => {
      const { idSuffix, kind, type, daysUntil, originalDateRaw, detailPrefix } =
        opts;

      const sev = lifeEventSeverityBucket(daysUntil);
      if (!sev) return; // ✅ hides 32+ and invalid

      const eventDate = parseDate(originalDateRaw);
      const eventDateLabel = eventDate
        ? `${eventDate.getMonth() + 1}/${eventDate.getDate()}`
        : "upcoming";

      const withinWeek = daysUntil <= 7;

      alerts.push({
        id: `${idSuffix}-${c.id || idx}`,
        kind,
        type,
        severity: sev,
        clientId: c.id,
        clientName: name,
        stage: stageLabel,
        clientType: typeLabel || "Client",
        property,
        createdAt: now.toLocaleString(),
        summary: `${name}'s ${type.toLowerCase()} is in ${daysUntil} day${
          daysUntil === 1 ? "" : "s"
        } (${eventDateLabel}).`,
        detail:
          (detailPrefix ||
            `Important client life event coming up (${type.toLowerCase()}). `) +
          (withinWeek
            ? "Recommend sending a card or small gift to stay top of mind."
            : "Plan a simple touchpoint or card so they feel remembered."),
        source: "Life event",
        status: "Open",
        lastContact,
        daysSinceContact,
        daysToClose: null,
        createdAtRaw: now.toISOString(),
        closeDateRaw: null,
        daysUntilEvent: daysUntil,
        persisted: false,
      });
    };

    if (daysUntilBirthday != null) {
      pushLifeEvent({
        idSuffix: "BDAY",
        kind: "BIRTHDAY",
        type: "Birthday",
        daysUntil: daysUntilBirthday,
        originalDateRaw: birthdayRaw,
        detailPrefix: "Client birthday is coming up. ",
      });
    }

    if (daysUntilAnniversary != null) {
      pushLifeEvent({
        idSuffix: "ANNIV",
        kind: "ANNIVERSARY",
        type: "Anniversary",
        daysUntil: daysUntilAnniversary,
        originalDateRaw: anniversaryRaw,
        detailPrefix: "Client home / relationship anniversary is coming up. ",
      });
    }
  });

  // Sort: Priority -> High -> Medium -> Low, then newest first
  const sevOrder = { Priority: 0, High: 1, Medium: 2, Low: 3 };
  return alerts.sort((a, b) => {
    const sA = sevOrder[a.severity] ?? 99;
    const sB = sevOrder[b.severity] ?? 99;
    if (sA !== sB) return sA - sB;
    const dA = parseDate(a.createdAtRaw) || new Date(0);
    const dB = parseDate(b.createdAtRaw) || new Date(0);
    return dB.getTime() - dA.getTime();
  });
}

/* -------------------- normalize BACKEND alerts -> UI shape -------------------- */

function normalizeBackendAlerts(rawAlerts, clientsById) {
  const now = new Date();
  const list = Array.isArray(rawAlerts) ? rawAlerts : [];

  return list
    .map((a, idx) => {
      // Backend returns persisted alerts with numeric string id (AlertEvent.id),
      // plus computed alerts with ids like "match_..."
      const rawId = a?.id != null ? String(a.id) : `BACK-${idx}`;

      const isNumericId = /^\d+$/.test(rawId);
      const persisted = isNumericId; // ✅ A: ONLY these can be dismissed persistently

      // Basic fields from backend
      const type = a?.type || "Alert";
      const sourceType = a?.source_type || "";
      const daysUntil =
        typeof a?.days_until === "number" ? a.days_until : null;

      // Identify kind for icons
      let kind = "OTHER";
      const tl = String(type).toLowerCase();
      if (tl.includes("birthday") || String(sourceType).toLowerCase() === "birthday") kind = "BIRTHDAY";
      else if (tl.includes("anniversary") || String(sourceType).toLowerCase() === "anniversary") kind = "ANNIVERSARY";
      else if (tl.includes("follow-up") || String(sourceType).toLowerCase() === "follow_up") kind = "FOLLOW_UP";
      else if (tl.includes("upcoming event") || String(sourceType).toLowerCase() === "deal_event") kind = "CLOSING_SOON";

      // Try to resolve client name from backend client_id (preferred)
      const clientId =
        a?.client_id != null ? Number(a.client_id) : null;
      const client = clientId != null ? clientsById?.get(clientId) : null;

      const clientName =
        a?.client_name ||
        (client ? formatName(client) : "") ||
        "Client";

      const stage = a?.stage || client?.stage || "Active";

      const createdAtIso = a?.created_at || now.toISOString();
      const createdAtDt = parseDate(createdAtIso) || now;

      // ✅ Life-event windows/severity enforced here too
      let severity = normalizeSeverity(a?.severity);
      if (kind === "BIRTHDAY" || kind === "ANNIVERSARY") {
        const sev = lifeEventSeverityBucket(daysUntil);
        if (!sev) return null; // hide 32+ or invalid
        severity = sev;
      }

      const summary = a?.summary || "";
      const detail = a?.message || a?.summary || "Generated by AgentBlayna Alerts engine.";

      return {
        id: persisted ? `BE-${rawId}` : `BE-${rawId}`, // UI id
        backendId: persisted ? Number(rawId) : null,   // numeric for dismiss endpoint
        persisted,
        kind,
        type,
        severity,
        clientId: client?.id ?? clientId ?? null,
        clientName,
        stage,
        clientType: client?.client_type || "Client",
        property:
          client?.property_address ||
          client?.listing_address ||
          client?.search_area ||
          "",
        createdAt: createdAtDt.toLocaleString(),
        summary,
        detail,
        source: persisted ? "Backend alerts" : "Computed alerts",
        status: a?.status || "Open",
        lastContact: parseDate(
          client?.last_contacted_at ||
            client?.last_contact ||
            client?.last_contact_date ||
            client?.last_touched_at
        ),
        daysSinceContact: (() => {
          const lc = parseDate(
            client?.last_contacted_at ||
              client?.last_contact ||
              client?.last_contact_date ||
              client?.last_touched_at
          );
          return lc ? daysBetween(new Date(), lc) : null;
        })(),
        daysToClose: null,
        createdAtRaw: createdAtDt.toISOString(), // ✅ for relative time
        closeDateRaw: null,
        daysUntilEvent: daysUntil,

        // ✅ ADDITIVE: keep backend hint available for event label resolution
        source_type: sourceType,
      };
    })
    .filter(Boolean);
}

/* -------------------- UI helpers -------------------- */

const TypeIcon = ({ kind }) => {
  if (kind === "NEW_LEAD")
    return <PersonAddAltRoundedIcon fontSize="small" color="primary" />;
  if (kind === "FOLLOW_UP")
    return <PhoneInTalkRoundedIcon fontSize="small" color="secondary" />;
  if (kind === "CLOSING_SOON")
    return <EventAvailableRoundedIcon fontSize="small" color="success" />;
  if (kind === "BIRTHDAY")
    return <CakeRoundedIcon fontSize="small" color="secondary" />;
  if (kind === "ANNIVERSARY")
    return <FavoriteRoundedIcon fontSize="small" color="error" />;
  return <ErrorOutlineRoundedIcon fontSize="small" color="warning" />;
};

const SeverityChip = ({ level }) => {
  const map = {
    Priority: { color: "error", label: "Priority" },
    High: { color: "error", label: "High" },
    Medium: { color: "warning", label: "Medium" },
    Low: { color: "success", label: "Low" },
  };
  const cfg = map[level] || map.Medium;
  return <Chip size="small" color={cfg.color} label={cfg.label} />;
};

const StatusChip = ({ status }) => {
  const map = {
    Open: { color: "secondary", label: "Open" },
    Done: { color: "success", label: "Done" },
    Snoozed: { color: "default", label: "Snoozed" },
  };
  const cfg = map[status] || map.Open;
  return (
    <Chip size="small" variant="outlined" color={cfg.color} label={cfg.label} />
  );
};

// Compact pill buttons
const ACTION_BUTTON_SX = {
  borderRadius: 999,
  textTransform: "none",
  px: 1.8,
  py: 0.5,
  fontSize: 12,
  minWidth: 110,
  display: "inline-flex",
  justifyContent: "center",
};

/* -------------------- Alert Card -------------------- */

function AlertCard({
  alert,
  checked,
  onToggle,
  onDone,
  onSnooze,
  onReopen,
  onOpen,
  nowTick,
}) {
  const status = alert.status || "Open";

  const showMarkDone = status === "Open" || status === "Snoozed";
  const showSnooze = status === "Open";
  const showReopen = status === "Snoozed" || status === "Done";

  const liveStamp =
    alert.createdAtRaw ? relativeTimeFromIso(alert.createdAtRaw, nowTick) : alert.createdAt;

  return (
    <Paper sx={{ p: 2, borderRadius: 2, height: "100%" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Checkbox
            size="small"
            checked={checked}
            onChange={() => onToggle(alert.id)}
          />
          <SeverityChip level={alert.severity} />
          <Typography
            variant="subtitle1"
            fontWeight={800}
            sx={{ cursor: "pointer" }}
            onClick={() => onOpen(alert.id)}
          >
            {alert.title || alert.type}
          </Typography>
          <StatusChip status={status} />
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {liveStamp}
        </Typography>
      </Stack>

      <Divider sx={{ my: 1.25 }} />

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={8}>
          <Stack spacing={0.4} color="text.secondary">
            <Stack direction="row" spacing={1} alignItems="center">
              <TypeIcon kind={alert.kind} />
              <Typography variant="body2" color="text.secondary">
                <strong>{alert.type}</strong> • {alert.clientName}
              </Typography>
            </Stack>
            <Typography variant="body2">
              <strong>Stage:</strong> {alert.stage}
            </Typography>
            {alert.property && (
              <Typography variant="body2">
                <strong>Property:</strong> {alert.property}
              </Typography>
            )}
            <Typography variant="body2">
              <strong>Summary:</strong> {alert.summary}
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack
            direction="row"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            spacing={1}
            flexWrap="wrap"
            rowGap={1}
          >
            {showMarkDone && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<DoneAllRoundedIcon />}
                onClick={() => onDone(alert.id)}
                sx={ACTION_BUTTON_SX}
              >
                Mark done
              </Button>
            )}

            {showSnooze && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<SnoozeRoundedIcon />}
                onClick={() => onSnooze(alert.id)}
                sx={ACTION_BUTTON_SX}
              >
                Snooze
              </Button>
            )}

            {showReopen && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => onReopen(alert.id)}
                sx={ACTION_BUTTON_SX}
              >
                Re-open
              </Button>
            )}

            <Tooltip title="Open details">
              <IconButton
                size="small"
                color="secondary"
                onClick={() => onOpen(alert.id)}
              >
                <OpenInNewRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

/* -------------------- Drawer -------------------- */

function AlertDrawer({
  open,
  onClose,
  alert,
  aiLoading,
  aiText,
  onDraftMessage,
  nowTick,
}) {
  if (!alert) return null;

  const daysContactLabel =
    alert.daysSinceContact != null
      ? `${alert.daysSinceContact} days since last contact`
      : "Last contact date not set";

  let closingLabel = "No close date set";
  if (alert.kind === "BIRTHDAY" || alert.kind === "ANNIVERSARY") {
    if (alert.daysUntilEvent != null) {
      closingLabel = `${alert.type} in ${alert.daysUntilEvent} day${
        alert.daysUntilEvent === 1 ? "" : "s"
      }`;
    } else {
      closingLabel = `${alert.type} date not set`;
    }
  } else if (alert.daysToClose != null) {
    if (alert.daysToClose < 0) {
      closingLabel = `Closed ${Math.abs(
        alert.daysToClose
      )} days ago (based on expected date)`;
    } else {
      closingLabel = `Closing in ${alert.daysToClose} days`;
    }
  }

  const liveStamp =
    alert.createdAtRaw ? relativeTimeFromIso(alert.createdAtRaw, nowTick) : alert.createdAt;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 420, p: 2 } }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <SeverityChip level={alert.severity} />
          <Typography variant="h6" fontWeight={800}>
            {alert.type}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {alert.id} • {liveStamp}
      </Typography>

      <Divider sx={{ my: 1.5 }} />

      <Paper sx={{ p: 1.5, mb: 2 }} variant="outlined">
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar>
            <PersonRoundedIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle2" fontWeight={800}>
              {alert.clientName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {alert.clientType || "Client"} • {alert.stage}
            </Typography>
            {alert.property && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {alert.property}
              </Typography>
            )}
          </Box>
        </Stack>
      </Paper>

      <Stack spacing={0.75} mb={2}>
        <Typography variant="body2">
          <strong>Summary:</strong> {alert.summary}
        </Typography>
        <Typography variant="body2">
          <strong>Detail:</strong> {alert.detail}
        </Typography>
        <Typography variant="body2">
          <strong>Timing:</strong> {daysContactLabel} • {closingLabel}
        </Typography>
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
        Let AgentBlayna draft the outreach
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        AgentBlayna will write a short text or email you can send to this
        client based on their stage and what this alert is about.
      </Typography>

      <Button
        fullWidth
        variant="contained"
        color="primary"
        startIcon={<ChatRoundedIcon />}
        disabled={aiLoading}
        onClick={() => onDraftMessage(alert)}
        sx={{ mb: 1.5 }}
      >
        {aiLoading ? "Drafting message…" : "Draft message with AgentBlayna"}
      </Button>

      <Paper sx={{ p: 1.5 }} variant="outlined">
        <Typography
          variant="body2"
          color="text.primary"
          sx={{ whiteSpace: "pre-wrap" }}
        >
          {aiLoading
            ? "Thinking through the right tone, timing, and next step…"
            : aiText ||
              "Your AI-generated outreach message will appear here. You can copy it into text, email, or your dialer."}
        </Typography>
      </Paper>
    </Drawer>
  );
}

/* -------------------- ADDITIVE UI components (NEW) -------------------- */

function ClientSeveritySummary({ alerts }) {
  const counts = summarizeSeverities(alerts);
  const label = formatSeveritySummary(counts);
  if (!label) return null;

  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      sx={{ borderRadius: 999, fontWeight: 700 }}
    />
  );
}

function highestSeverity(alerts) {
  const sevOrder = { Priority: 0, High: 1, Medium: 2, Low: 3 };
  let best = "Medium";
  let bestScore = 99;
  (alerts || []).forEach((a) => {
    const s = sevOrder[a?.severity] ?? 99;
    if (s < bestScore) {
      bestScore = s;
      best = a.severity;
    }
  });
  return best;
}

function ClientGroupCard({
  clientKey,
  clientName,
  subline,
  alerts,
  expanded,
  onToggleExpand,
  selected,
  onToggleOne,
  onDone,
  onSnooze,
  onReopen,
  onOpen,
  nowTick,
}) {
  const topSeverity = highestSeverity(alerts);
  const total = alerts.length;

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 2,
        background:
          topSeverity === "Priority"
            ? "rgba(244,67,54,0.06)"
            : topSeverity === "High"
            ? "rgba(244,67,54,0.04)"
            : "transparent",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar sx={{ width: 38, height: 38 }}>
          <PersonRoundedIcon />
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
            <Typography
              variant="subtitle1"
              fontWeight={900}
              sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {clientName}
            </Typography>
            <SeverityChip level={topSeverity} />
            <Chip
              size="small"
              variant="outlined"
              label={`${total} alert${total === 1 ? "" : "s"}`}
              sx={{ borderRadius: 999, fontWeight: 700 }}
            />
            <ClientSeveritySummary alerts={alerts} />
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {subline}
          </Typography>
        </Box>

        <Tooltip title={expanded ? "Collapse" : "Expand"}>
          <IconButton size="small" onClick={() => onToggleExpand(clientKey)}>
            {expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 1.5 }} />
        <Grid container spacing={2}>
          {alerts.map((a) => (
            <Grid key={a.id} item xs={12} md={6}>
              <AlertCard
                alert={a}
                checked={!!selected[a.id]}
                onToggle={onToggleOne}
                onDone={(id) => onDone([id])}
                onSnooze={(id) => onSnooze([id])}
                onReopen={(id) => onReopen([id])}
                onOpen={onOpen}
                nowTick={nowTick}
              />
            </Grid>
          ))}
        </Grid>
      </Collapse>
    </Paper>
  );
}

/* -------------------- Page -------------------- */

export default function Alerts() {
  const outletCtx = useOutletContext() || {};
  const { onAiStatusChange, aiRefreshToken } = outletCtx;

  const [alerts, setAlerts] = useState([]);
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState({});
  const [severity, setSeverity] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [status, setStatus] = useState("Open");
  const [q, setQ] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeAlert, setActiveAlert] = useState(null);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiAlertId, setAiAlertId] = useState(null);

  // ✅ tick for relative timestamps (updates naturally while page is open)
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNowTick(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const relativeLastUpdated = useMemo(() => {
    if (!lastUpdated) return "never";
    const diffMs = Date.now() - lastUpdated.getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin <= 0) return "just now";
    if (diffMin === 1) return "1 minute ago";
    if (diffMin < 60) return `${diffMin} minutes ago`;
    const diffHours = Math.round(diffMin / 60);
    if (diffHours === 1) return "1 hour ago";
    return `${diffHours} hours ago`;
  }, [lastUpdated]);

  const severityCounts = useMemo(() => {
    const base = { Priority: 0, High: 0, Medium: 0, Low: 0 };
    alerts.forEach((a) => {
      if (base[a.severity] != null) base[a.severity] += 1;
    });
    return base;
  }, [alerts]);

  const fetchClients = useCallback(async () => {
    const res = await apiFetch(API_ENDPOINTS.CLIENTS);
    if (!res.ok) throw new Error(`Clients HTTP ${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }, []);

  const fetchBackendAlerts = useCallback(async () => {
    const res = await apiFetch("/api/alerts/");
    if (!res.ok) throw new Error(`Alerts HTTP ${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }, []);

  async function loadFromBackend() {
    try {
      setLoading(true);
      setLoadError(null);

      // 1) Fetch clients (primary data)
      const clientsList = await fetchClients();
      setClients(clientsList);

      const clientsById = new Map();
      clientsList.forEach((c) => {
        if (c?.id != null) clientsById.set(Number(c.id), c);
      });

      // 2) Fetch backend alerts ONLY (persisted + computed)
let backendAlerts = [];
try {
  const rawBackend = await fetchBackendAlerts();
  backendAlerts = normalizeBackendAlerts(rawBackend, clientsById);
} catch (e) {
  console.warn("Backend alerts fetch failed, continuing:", e);
}

// ✅ SINGLE SOURCE OF TRUTH: backend alerts only
const merged = backendAlerts
  .map(resolveEventLabel)
  .sort((a, b) => {
    const sevOrder = { Priority: 0, High: 1, Medium: 2, Low: 3 };
    const sA = sevOrder[a.severity] ?? 99;
    const sB = sevOrder[b.severity] ?? 99;
    if (sA !== sB) return sA - sB;
    const dA = parseDate(a.createdAtRaw) || new Date(0);
    const dB = parseDate(b.createdAtRaw) || new Date(0);
    return dB.getTime() - dA.getTime();
  });

setAlerts(merged);

      setSelected({});
    } catch (e) {
      console.error("Alert load error:", e);
      setLoadError(
        "Unable to load live alerts. Check your /api/clients/ and /api/alerts/ endpoints."
      );
      setAlerts([]);
      setSelected({});
    } finally {
      const now = new Date();
      setLoading(false);
      setLastUpdated(now);
      onAiStatusChange?.({ loading: false, lastUpdated: now });
    }
  }

  useEffect(() => {
    loadFromBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (aiRefreshToken == null) return;
    loadFromBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiRefreshToken]);

  const filtered = useMemo(() => {
    return alerts.filter((a) => {
      if (severity !== "All" && a.severity !== severity) return false;

      if (typeFilter === "Birthdays & anniversaries") {
        if (a.type !== "Birthday" && a.type !== "Anniversary") return false;
      } else if (typeFilter !== "All" && a.type !== typeFilter) {
        return false;
      }

      if (status !== "All" && (a.status || "Open") !== status) return false;

      if (
        q &&
        !`${a.type} ${a.clientName} ${a.summary} ${a.detail} ${a.property}`
          .toLowerCase()
          .includes(q.toLowerCase())
      )
        return false;

      return true;
    });
  }, [alerts, severity, typeFilter, status, q]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const toggleOne = (id) =>
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleAll = () => {
    if (selectedIds.length === filtered.length) setSelected({});
    else {
      const next = {};
      filtered.forEach((a) => (next[a.id] = true));
      setSelected(next);
    }
  };

  const updateStatus = (ids, newStatus) => {
    if (!ids.length) return;
    setAlerts((list) =>
      list.map((a) => (ids.includes(a.id) ? { ...a, status: newStatus } : a))
    );
    setSelected((prev) => {
      const copy = { ...prev };
      ids.forEach((id) => delete copy[id]);
      return copy;
    });
  };

  // ✅ A: Persisted dismiss (backend only). Non-persisted alerts still use local status.
  const dismissPersistedBackendAlerts = useCallback(
    async (ids) => {
      // Build lookup of selected alert objects
      const idSet = new Set(ids);
      const targets = alerts.filter((a) => idSet.has(a.id));

      const persistedTargets = targets.filter((a) => a?.persisted && a?.backendId);
      const localTargets = targets.filter((a) => !a?.persisted);

      // 1) Persisted: POST /api/alerts/{id}/dismiss then remove from UI (won't return on refresh)
      if (persistedTargets.length) {
        await Promise.all(
          persistedTargets.map(async (a) => {
            try {
              const res = await apiFetch(`/api/alerts/${a.backendId}/dismiss`, {
                method: "POST",
              });
              if (!res.ok) {
                console.warn("Dismiss failed:", a.backendId, res.status);
              }
            } catch (e) {
              console.warn("Dismiss error:", e);
            }
          })
        );

        // Optimistic UI: remove dismissed persisted alerts immediately
        setAlerts((list) => list.filter((x) => !persistedTargets.some((p) => p.id === x.id)));
      }

      // 2) Local: just mark done (still visible if status filter includes Done)
      if (localTargets.length) {
        updateStatus(localTargets.map((a) => a.id), "Done");
      }

      setSelected({});
    },
    [alerts]
  );

  const markDone = (ids) => dismissPersistedBackendAlerts(ids);
  const snooze = (ids) => updateStatus(ids, "Snoozed");
  const reopen = (ids) => updateStatus(ids, "Open");

  const openOne = (id) => {
    const found = alerts.find((a) => a.id === id);
    setActiveAlert(found || null);
    setDrawerOpen(true);
    setAiText("");
    setAiAlertId(null);
  };

  const handleExportClick = () => {
    if (!filtered.length) return;

    const rows = [
      [
        "ID",
        "Type",
        "Severity",
        "Status",
        "Client",
        "Stage",
        "Client Type",
        "Property",
        "Summary",
        "Created At",
        "Source",
      ],
      ...filtered.map((a) => [
        a.id,
        a.type,
        a.severity,
        a.status || "Open",
        a.clientName,
        a.stage,
        a.clientType,
        a.property || "",
        a.summary,
        a.createdAtRaw || a.createdAt || "",
        a.source || "",
      ]),
    ];

    const csv = rows.map((r) => r.map(safeCsvCell).join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "agentblayna_alerts.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const draftMessage = async (alert) => {
    try {
      setAiLoading(true);
      setAiAlertId(alert.id);
      setAiText("");
      onAiStatusChange?.({ loading: true });

      const payload = {
        model: "gpt-4o-mini",
        messages: [
          {
            role: "system",
            content:
              "You are AgentBlayna, an AI assistant for real estate agents. " +
              "Write short, friendly, concrete outreach messages (2–4 sentences) based on the situation.",
          },
          {
            role: "user",
            content: `
Alert type: ${alert.type}
Client name: ${alert.clientName}
Stage: ${alert.stage}
Client type: ${alert.clientType || "Unknown"}
Property: ${alert.property || "Not specified"}
Summary: ${alert.summary}
Detail: ${alert.detail}
Days since last contact: ${
              alert.daysSinceContact != null ? alert.daysSinceContact : "unknown"
            }
Days until closing or event (if any): ${
              alert.daysToClose != null
                ? alert.daysToClose
                : alert.daysUntilEvent != null
                ? alert.daysUntilEvent
                : "n/a"
            }

Write a short message I can send as a text or email. Use a professional but warm tone, and end with a clear next step or question.`,
          },
        ],
      };

      const res = await apiFetch(API_ENDPOINTS.CHAT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let errDetail = `HTTP ${res.status}`;
        try {
          const errBody = await res.json();
          if (errBody?.detail) errDetail = errBody.detail;
        } catch (_) {}
        throw new Error(errDetail);
      }
      const data = await res.json();

      const reply =
        typeof data?.reply === "string"
          ? data.reply
          : typeof data?.reply?.content === "string"
          ? data.reply.content
          : "I couldn't generate a message. Try again in a moment.";

      setAiText(reply);
    } catch (e) {
      console.error("Alert AI draft error:", e);
      setAiText(
        e.message && !e.message.startsWith("HTTP")
          ? `AgentBlayna: ${e.message}`
          : "AgentBlayna couldn't connect to the AI engine. Please try again."
      );
    } finally {
      setAiLoading(false);
      const now = new Date();
      onAiStatusChange?.({ loading: false, lastUpdated: now });
    }
  };

  // ✅ ADDITIVE: client grouping structure for final render swap only
  const groupedByClient = useMemo(() => {
    const groups = new Map();
    filtered.forEach((a) => {
      const key = a.clientId != null ? `ID:${a.clientId}` : `NAME:${a.clientName}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(a);
    });

    const sevOrder = { Priority: 0, High: 1, Medium: 2, Low: 3 };
    const result = Array.from(groups.entries()).map(([key, list]) => {
      const sorted = [...list].sort((x, y) => {
        const sx = sevOrder[x.severity] ?? 99;
        const sy = sevOrder[y.severity] ?? 99;
        if (sx !== sy) return sx - sy;
        const dx = parseDate(x.createdAtRaw) || new Date(0);
        const dy = parseDate(y.createdAtRaw) || new Date(0);
        return dy.getTime() - dx.getTime();
      });

      const top = sorted[0];
      const subline = `${top.clientType || "Client"} • ${top.stage || "Active"}`;

      return {
        key,
        clientName: top.clientName,
        subline,
        alerts: sorted,
      };
    });

    // Sort client cards by their highest priority alert
    return result.sort((a, b) => {
      const aTop = highestSeverity(a.alerts);
      const bTop = highestSeverity(b.alerts);
      const rank = { Priority: 0, High: 1, Medium: 2, Low: 3 };
      const ra = rank[aTop] ?? 99;
      const rb = rank[bTop] ?? 99;
      if (ra !== rb) return ra - rb;

      const da = parseDate(a.alerts?.[0]?.createdAtRaw) || new Date(0);
      const db = parseDate(b.alerts?.[0]?.createdAtRaw) || new Date(0);
      return db.getTime() - da.getTime();
    });
  }, [filtered]);

  const [expandedClients, setExpandedClients] = useState({});
  const toggleExpandClient = (key) =>
    setExpandedClients((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Stack spacing={2.5}>
      {loading && (
        <Typography variant="body2" color="text.secondary">
          Scanning your clients for follow-ups, closings, and life events…
        </Typography>
      )}
      {loadError && !loading && (
        <Typography variant="body2" color="warning.main">
          {loadError}
        </Typography>
      )}

      {/* Filters / controls */}
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search alerts or clients…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={2.5}>
            <TextField
              fullWidth
              select
              size="small"
              label="Severity"
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
            >
              {["All", "Priority", "High", "Medium", "Low"].map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2.5}>
            <TextField
              fullWidth
              select
              size="small"
              label="Type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              {[
                "All",
                "New lead",
                "Follow-up",
                "Deal closing soon",
                "Buyer match",
                "Upcoming event",
                "Birthday",
                "Anniversary",
                "Birthdays & anniversaries",
                "Open house",
                "Photography",
                "Inspection",
                "Appraisal",
                "Final walkthrough",
              ].map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2.5}>
            <TextField
              fullWidth
              select
              size="small"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {["Open", "Done", "Snoozed", "All"].map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={12}
            md={3}
            sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FileDownloadRoundedIcon />}
                onClick={handleExportClick}
                size="small"
              >
                Export
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<BoltRoundedIcon />}
                onClick={loadFromBackend}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                Refresh
              </Button>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Chip
                size="small"
                color="primary"
                icon={<BoltRoundedIcon />}
                label="AgentBlayna watching"
                sx={{ fontWeight: 600 }}
              />
              <Typography variant="caption" color="text.secondary">
                Last updated: {loading ? "updating…" : relativeLastUpdated}
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Checkbox
            size="small"
            checked={selectedIds.length === filtered.length && filtered.length > 0}
            indeterminate={
              selectedIds.length > 0 && selectedIds.length < filtered.length
            }
            onChange={toggleAll}
          />
          <Typography variant="body2" color="text.secondary">
            {selectedIds.length} selected
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<DoneAllRoundedIcon />}
            disabled={selectedIds.length === 0}
            onClick={() => markDone(selectedIds)}
            sx={ACTION_BUTTON_SX}
          >
            Mark done
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<SnoozeRoundedIcon />}
            disabled={selectedIds.length === 0}
            onClick={() => snooze(selectedIds)}
            sx={ACTION_BUTTON_SX}
          >
            Snooze
          </Button>
        </Stack>

        {alerts.length > 15 && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            mt={2}
            flexWrap="wrap"
          >
            <Typography variant="caption" color="text.secondary">
              Triage by priority:
            </Typography>
            {["Priority", "High", "Medium", "Low"].map((level) => (
              <Chip
                key={level}
                size="small"
                variant={severity === level ? "filled" : "outlined"}
                color={
                  level === "Priority"
                    ? "error"
                    : level === "High"
                    ? "error"
                    : level === "Medium"
                    ? "warning"
                    : "success"
                }
                label={`${level} • ${severityCounts[level] || 0}`}
                onClick={() =>
                  setSeverity((prev) => (prev === level ? "All" : level))
                }
              />
            ))}
          </Stack>
        )}
      </Paper>

      {/* Alerts list */}
      {filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={800}>
            No alerts match your filters
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Once you add clients and keep stages and dates updated, AgentBlayna
            will generate alerts for new leads, follow-ups, closings, and
            birthdays/anniversaries.
          </Typography>
        </Paper>
      ) : (
        // ✅ RENDER SWAP ONLY: 1 card per client, expandable
        <Stack spacing={2}>
          {groupedByClient.map((g) => (
            <ClientGroupCard
              key={g.key}
              clientKey={g.key}
              clientName={g.clientName}
              subline={g.subline}
              alerts={g.alerts}
              expanded={!!expandedClients[g.key]}
              onToggleExpand={toggleExpandClient}
              selected={selected}
              onToggleOne={toggleOne}
              onDone={markDone}
              onSnooze={snooze}
              onReopen={reopen}
              onOpen={openOne}
              nowTick={nowTick}
            />
          ))}
        </Stack>
      )}

      <AlertDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        alert={activeAlert}
        aiLoading={aiLoading && aiAlertId === activeAlert?.id}
        aiText={aiAlertId === activeAlert?.id ? aiText : ""}
        onDraftMessage={draftMessage}
        nowTick={nowTick}
      />
    </Stack>
  );
}
